/**
 * useAgents — the single client wrapper for the Agents Portal callable
 * (hidden /agents section, SuperAdmin only).
 *
 *   callAgents('listAgents')
 *   callAgents('runNow', { id })
 *   callAgents('listFindings', { status: 'new' })
 *
 * One action-style callable (agentsManage, us-east1) backs the whole portal,
 * mirroring useDocAI. useRunPoller polls a run until it reaches a terminal
 * status, exposing live progress + streamed log events along the way.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functionsUsEast1 } from '../utils/init-firebase';

/** Call the agents gateway with an action. Throws Error(message) on failure. */
export async function callAgents(action, payload = {}) {
  const fn = httpsCallable(functionsUsEast1, 'agentsManage');
  try {
    const res = await fn({ action, ...payload });
    return res.data;
  } catch (err) {
    throw new Error(err?.message || 'Agents call failed');
  }
}

const TERMINAL = ['done', 'error', 'cancelled'];

/**
 * Poll a run (and its events) until terminal. Returns
 * { run, events, isPolling, refresh }. Pass null runId to idle.
 */
export function useRunPoller(runId, { intervalMs = 2500 } = {}) {
  const [run, setRun] = useState(null);
  const [events, setEvents] = useState([]);
  const [isPolling, setIsPolling] = useState(false);
  const timerRef = useRef(null);
  const runIdRef = useRef(runId);
  runIdRef.current = runId;

  const refresh = useCallback(async () => {
    if (!runIdRef.current) return null;
    const data = await callAgents('getRun', { runId: runIdRef.current });
    setRun(data.run);
    setEvents(data.events || []);
    return data.run;
  }, []);

  useEffect(() => {
    if (!runId) {
      setRun(null);
      setEvents([]);
      setIsPolling(false);
      return undefined;
    }
    let alive = true;
    setIsPolling(true);

    const tick = async () => {
      try {
        const current = await refresh();
        if (!alive) return;
        if (current && TERMINAL.includes(current.status)) {
          setIsPolling(false);
          return; // stop polling
        }
      } catch (_) {
        // Transient poll errors: keep trying.
      }
      if (alive) timerRef.current = setTimeout(tick, intervalMs);
    };
    tick();

    return () => {
      alive = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [runId, intervalMs, refresh]);

  return { run, events, isPolling, refresh };
}

export default callAgents;
